import { useState } from 'react';
import { Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { callBackend } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useOptionalCrm } from './CrmContext';
import { errorMessage } from './format';

/**
 * Call a number. With click-to-call on, the PBX rings my extension and then dials the number;
 * otherwise a tel: link (softphone or mobile).
 */
export function CallButton({ number, label, size = 'md' }: { number: string; label?: string; size?: 'sm' | 'md' }) {
  const crm = useOptionalCrm();
  const [calling, setCalling] = useState(false);
  const viaPbx = Boolean(crm?.data?.features.clickToCall);
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const classes =
    size === 'sm'
      ? 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-xs font-medium'
      : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium';

  if (!viaPbx) {
    return (
      <a href={`tel:${number.replace(/[^\d+]/g, '')}`} className={classes} title={`Call ${number}`}>
        <Phone className={iconSize} />
        {label}
      </a>
    );
  }

  const dial = async () => {
    if (!crm?.data?.prefs.extension) {
      toast.error('Set your extension in CRM → My settings first.');
      return;
    }
    setCalling(true);
    try {
      const result = await callBackend<{ ok: boolean; message: string }>(backendApi.crmPbx.clickToCall, { number });
      if (result.ok) toast.success(result.message, { duration: 6000 });
      else toast.error(result.message);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCalling(false);
    }
  };

  return (
    <button type="button" onClick={() => void dial()} disabled={calling} className={classes} title={`Call ${number} from your phone`}>
      <Phone className={`${iconSize} ${calling ? 'animate-pulse' : ''}`} />
      {label}
    </button>
  );
}
